import { type Ref } from "vue";
import { onKeyStroke } from "@vueuse/core";
import type { DSBaseListOption } from "../types";
import { findOptionById, getOptionId } from "../utils/optionListUtils";
import { getNextIndexByStep, scrollActiveOptionIntoView } from "../../shared/utils/keyboardNavigation";

export interface UseOptionListKeyboardNavigationOptions {
  listRef: Ref<HTMLElement | undefined>;
  keyboardNavigationEnabled: Ref<boolean>;
  activeOptionId: Ref<string | undefined>;
  activeIndex: Ref<number>;
  flatOptions: Ref<DSBaseListOption[]>;
  resolvedListId: Ref<string>;
  selectOption: (option: DSBaseListOption) => void;
}

const SELECTION_KEYS = ["Enter", " "];

export const useOptionListKeyboardNavigation = ({
  listRef,
  keyboardNavigationEnabled,
  activeOptionId,
  activeIndex,
  flatOptions,
  resolvedListId,
  selectOption,
}: UseOptionListKeyboardNavigationOptions) => {
  const getNextActiveOptionId = (step: 1 | -1): string | undefined => {
    if (flatOptions.value.length === 0) return undefined;

    const nextIndex = getNextIndexByStep(activeIndex.value, flatOptions.value.length, step);

    return getOptionId(resolvedListId.value, flatOptions.value[nextIndex]);
  };

  onKeyStroke("ArrowDown", e => {
    if (!keyboardNavigationEnabled.value) return;
    e.preventDefault();
    activeOptionId.value = getNextActiveOptionId(1);
    scrollActiveOptionIntoView(activeOptionId.value);
  });

  onKeyStroke("ArrowUp", e => {
    if (!keyboardNavigationEnabled.value) return;
    e.preventDefault();
    activeOptionId.value = getNextActiveOptionId(-1);
    scrollActiveOptionIntoView(activeOptionId.value);
  });

  const selectActiveOption = (e: KeyboardEvent) => {
    if (!keyboardNavigationEnabled.value) return;
    if (!activeOptionId.value) return;
    e.preventDefault();
    const activeOption = findOptionById(resolvedListId.value, flatOptions.value, activeOptionId.value);
    if (!activeOption) return;
    selectOption(activeOption);
  };

  onKeyStroke(SELECTION_KEYS, e => {
    if (e.target === listRef.value) return;
    selectActiveOption(e);
  });

  // Consumers may stop Enter at the list root; a focused list selects before that happens.
  const onListKeydown = (e: KeyboardEvent) => {
    if (e.target !== listRef.value || !SELECTION_KEYS.includes(e.key)) return;
    selectActiveOption(e);
  };

  return { getNextActiveOptionId, onListKeydown };
};
